import React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import {
  Typography,
  Link,
  Grid,
  Box,
  LinearProgress,
  Alert
} from "@mui/material";

import {
  saveOrganizationRequested,
  resetSaveState,
  selectIsSaving,
  selectError,
  selectSucceeded,
  fetchOrganizationRequested,
  selectIsFinishedCreating
} from "stores/organizations/organizationsSlice";
import { useDispatch, useSelector } from "react-redux";
import Environments from "services/environments";
import { FormSelect, FormInput, FormButton } from "components";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: "none",
    marginTop: theme.spacing(4)
  },
  container: {
    "& .MuiFormControl-root": {
      paddingRight: theme.spacing(2),
      width: "100%"
      //paddingBottom: "24px"
    }
  },
  submitButton: {
    float: "right"
  },

  toSeparator: {
    paddingRight: "1rem"
  }
}));

const initialFormState = {
  environment: { value: "", label: "" },
  organization_name: "",
  subdomain: "",
  school_year_start: { value: 0, label: "" },
  school_year_end: { value: 0, label: "" },
  school_years: []
};

const CreateOrganization = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isSaving = useSelector(selectIsSaving);
  const error = useSelector(selectError);
  const savedSucceeded = useSelector(selectSucceeded);
  const isFinishedCreating = useSelector(selectIsFinishedCreating);

  const [formValues, setFormValues] = React.useState(initialFormState);
  const [link, setLink] = React.useState("");

  React.useEffect(() => {
    if (!isFinishedCreating) {
      setTimeout(() => dispatch(fetchOrganizationRequested()), 5000);
    }
  }, [dispatch, isFinishedCreating]);

  React.useEffect(() => {
    if (formValues.subdomain.length > 0) {
      setLink(Environments[formValues.environment].replace(
        "subdomain",
        formValues.subdomain
      ));
    }
  }, [dispatch, formValues.subdomain, formValues.environment]);

  React.useEffect(
    () => () => {
      dispatch(resetSaveState());
    },
    [dispatch]
  );

  const buildSchema = () =>
    yup.object().shape({
      environment: yup.object().required("Required").nullable(),
      organization_name: yup
        .string()
        .typeError("Required")
        .required("Required"),
      subdomain: yup.string().required("Required").nullable(),
      school_year_start: yup.object().required("Required").nullable(),
      school_year_end: yup.object().required("Required").nullable()
    });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: initialFormState,
    resolver: yupResolver(buildSchema())
  });

  const buildEnvironmentOptions = () => {
    const environmentOptions = Object.keys(Environments).map((environment) => {
      return { value: environment, label: environment };
    });

    environmentOptions.push({ value: "", label: "" });

    return environmentOptions;
  };

  const buildSeederOptions = () => {
    return [
      { value: "none", label: "Default" },
      { value: "single_site", label: "Single Site" }
    ];
  };

  const buildSchoolYearOptions = () => {
    const school_years = [
      {
        value: 0,
        label: ""
      }
    ];
    for (let year = 2018; year <= new Date().getFullYear() + 6; year++) {
      school_years.push({
        value: year,
        label: year.toString()
      });
    }
    return school_years;
  };

  const handleFieldChange = (input, newValue) => {
    const updatedformValues = {
      ...formValues,
      [input]: newValue
    };
    setFormValues(updatedformValues);
  };

  const handleCreateOrganization = (formData) => {
    let submitValues = formData;
    submitValues["environment"] = submitValues["environment"].value;
    submitValues["seed"] = submitValues["seed"].value;
    for (
      let year = submitValues["school_year_start"].value;
      year <= submitValues["school_year_end"].value;
      year++
    ) {
      submitValues["school_years"].push(year);
    }
    submitValues["school_years"] = [...new Set(submitValues["school_years"])];
    setFormValues(submitValues);
    delete submitValues["school_year_start"];
    delete submitValues["school_year_end"];

    dispatch(saveOrganizationRequested(submitValues));
  };

  const getErrorBanner = () => {
    if (error) {
      return (
        <Alert severity="error">
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      );
    }
  };

  const getSuccessBanner = () => {
    if (savedSucceeded && isFinishedCreating) {
      return (
        <Alert severity="success">
          Organization Created Successfully, visit:{" "}
          <Link target="_blank" href={link}>
            {link}
          </Link>
        </Alert>
      );
    }
  };

  const getProgressBanner = () => {
    if (!isFinishedCreating) {
      return (
        <>
          <Alert severity="info">Organization is being created.</Alert>
          <LinearProgress />
        </>
      );
    }
  };

  const renderForm = () => (
    <Grid container justifyContent="center">
      <Grid item xs={6}>
        <Box width="inherit" mt={2}>
          <form onSubmit={handleSubmit(handleCreateOrganization)}>
            <Grid
              container
              direction="column"
              spacing={2}
              className={classes.container}
            >
              <Grid item>
                <FormSelect
                  options={buildEnvironmentOptions()}
                  name="environment"
                  label="Environment"
                  errors={errors}
                  isDisabled={isSaving || !isFinishedCreating}
                  onChange={handleFieldChange}
                  control={control}
                  description="Environment where to create the organization"
                />
              </Grid>
              <Grid item xs={10} lg={6}>
                <FormInput
                  name="organization_name"
                  label="Organization Name"
                  errors={errors}
                  isDisabled={isSaving || !isFinishedCreating}
                  control={control}
                  variant="filled"
                  description="Display name for the customer"
                />
              </Grid>
              <Grid item xs={10} lg={6}>
                <FormInput
                  name="subdomain"
                  label="Subdomain"
                  errors={errors}
                  isDisabled={isSaving || !isFinishedCreating}
                  control={control}
                  variant="filled"
                  description="Unique subdomain for the URL"
                />
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  justifyContent={"space-between"}
                >
                  <Grid item flexGrow={2}>
                    <FormSelect
                      options={buildSchoolYearOptions()}
                      name="school_year_start"
                      label="Earliest Starting School Year"
                      errors={errors}
                      isDisabled={isSaving || !isFinishedCreating}
                      onChange={handleFieldChange}
                      control={control}
                      description="Organization's Earliest Available Starting School Year"
                    />
                  </Grid>
                  <Grid item className={classes.toSeparator}>
                    <Typography paddingTop={"3rem"}>to</Typography>
                  </Grid>
                  <Grid item flexGrow={2}>
                    <FormSelect
                      options={buildSchoolYearOptions()}
                      name="school_year_end"
                      label="Latest Starting School Year"
                      errors={errors}
                      isDisabled={isSaving || !isFinishedCreating}
                      onChange={handleFieldChange}
                      control={control}
                      description="Organization's Latest Available Starting School Year"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormSelect
                  options={buildSeederOptions()}
                  name="seed"
                  label="Seeder"
                  errors={errors}
                  isDisabled={isSaving || !isFinishedCreating}
                  onChange={handleFieldChange}
                  control={control}
                  description="Type of Enroll Organization to Create"
                />
              </Grid>
              <Grid item>
                <Box>
                  <FormButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    value={
                      isFinishedCreating
                        ? "Create Organization"
                        : "Creating Organization..."
                    }
                    isSubmitting={isSaving || !isFinishedCreating}
                    disableElevation
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center">
      <Grid container item justifyContent="space-between" xs={6}>
        <Grid item>
          <Box width="inherit" mt={3} mb={1}>
            <Typography variant="h4" align="center" color="primary">
              Create New Organization
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Grid container alignItems="center" justifyContent="center">
        <Grid item lg={6} sm={12}>
          {getErrorBanner()}
          {getSuccessBanner()}
          {getProgressBanner()}
        </Grid>
      </Grid>
      {renderForm()}
    </Grid>
  );
};

export default CreateOrganization;
